'use client';

import {
  createContext, useContext, useCallback, ReactNode, useMemo,
} from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';
import { getI18nLinkHref } from '@services/i18nPaths';
import { slugify } from '@utils/strings/slugify';

export type CandidateSearchFilters = {
  keyword?: string;
  locations?: string[];
  salary?: number;
  originalKeyword?: string;
  decomposedFilters?: ApiSchema<'JobSearchRequestFilter'>;
};

export type CandidateView =
  | 'new-search'
  | 'new-job-search-form'
  | 'browse-all'
  | { keyword: string }
  | { candidateSearch: CandidateSearchFilters }
  | { editSearch: CandidateSearchFilters };

type NavigateOptions = { replace?: boolean };

interface CandidateNavigationContextValue {
  currentView: CandidateView;
  setView: (view: CandidateView, options?: NavigateOptions) => void;
  navigateToNewSearch: () => void;
  navigateToNewJobSearchForm: () => void;
  navigateToBrowseAll: () => void;
  navigateToKeywordSearch: (keyword: string) => void;
  navigateToCandidateSearch: (filters: CandidateSearchFilters, options?: NavigateOptions) => void;
  navigateToEditSearch: (filters: CandidateSearchFilters) => void;
}

const CandidateNavigationContext = createContext<CandidateNavigationContextValue | undefined>(undefined);

interface CandidateNavigationProviderProps {
  children: ReactNode;
  /** SSR seed for the very first paint, before the client URL hooks resolve query params. The URL is the source of truth thereafter. */
  initialView?: CandidateView;
  locale: Locale;
}

/** Derives the candidate view from a path + search string (used for popstate/back-forward). */
const getViewFromUrl = (path: string, search: string): CandidateView => {
  const urlParams = new URLSearchParams(search);
  const isEditMode = urlParams.get('edit') === '1';

  // Backward compat: old keyword-search URL — treat as candidateSearch
  const keywordSearchMatch = path.match(/\/job-search\/([^/]+)/);
  if (keywordSearchMatch) {
    const keyword = decodeURIComponent(keywordSearchMatch[1]).replace(/-/g, ' ');
    return { candidateSearch: { keyword } };
  }

  // Candidate search/edit results: /locuri-de-munca[-in-CITY][/KEYWORD] or /jobs[-in-CITY][/KEYWORD]
  const candidateListMatch = path.match(/\/(?:locuri-de-munca|jobs)(?:-in-([^/]+?))?(?:\/([^/?]+))?(?=[/?]|$)/);
  if (candidateListMatch) {
    const locationSlug = candidateListMatch[1];
    const keywordSlug = candidateListMatch[2];
    const keyword = keywordSlug ? decodeURIComponent(keywordSlug).replace(/-/g, ' ') : undefined;
    const locations = locationSlug ? locationSlug.split(',').map((s) => decodeURIComponent(s)) : undefined;
    const filters = {
      ...(keyword && { keyword }),
      ...(locations?.length && { locations }),
    };
    // ?edit=1 signals the edit form should be shown, not results
    return isEditMode ? { editSearch: filters } : { candidateSearch: filters };
  }

  if (/\/bestie-chat(\/|$)/.test(path)) {
    return 'new-search';
  }

  if (/\/search/.test(path)) {
    const keyword = urlParams.get('keyword') ?? undefined;
    const locations = urlParams.getAll('location');
    if (keyword || locations.length) {
      return {
        editSearch: {
          ...(keyword && { keyword }),
          ...(locations.length && { locations }),
        },
      };
    }
    return 'new-job-search-form';
  }

  // Default: /mc and any unmatched MC path resolve to the search form so the
  // candidate's landing matches the page component's initialView.
  return 'new-job-search-form';
};

/**
 * @description Provider for candidate navigation state management.
 *
 * View state is initialised from the `initialView` prop (set by the server page component
 * based on the current URL). Client-side navigation calls `setView` which updates state
 * immediately and then pushes the matching URL. Browser back/forward is handled via a
 * `popstate` listener so the effect cannot race with explicit `setView` calls.
 */
export const CandidateNavigationProvider = (props: CandidateNavigationProviderProps) => {
  const { children, locale, initialView = 'new-search' } = props;

  const { router } = useExtendedRouter();
  const pathname = usePathname() ?? '';
  const searchParams = useSearchParams();
  const search = searchParams?.toString() ?? '';

  // Single source of truth: the URL. The current view is derived from the path +
  // query, so soft navigation (setView -> router.push) drives the rendered content
  // and back/forward works for free — no parallel state machine to desync.
  // `initialView` only seeds the very first paint before the client URL hooks resolve.
  const currentView = useMemo(
    () => getViewFromUrl(pathname, search) || initialView,
    [pathname, search, initialView],
  );

  // Build the MC URL that represents a given candidate view.
  const buildViewUrl = useCallback((view: CandidateView): string => {
    if (view === 'new-search') return getI18nLinkHref('/bestie-chat', locale);
    if (view === 'browse-all') return getI18nLinkHref('/job/list', locale);
    // `/mc` is the canonical candidate landing and renders the search form,
    // so we point the bare form view there instead of `/mc/search`. The
    // `/mc/search` route still exists for deep links carrying `?keyword=…`
    // (which resolve to `editSearch` via getViewFromUrl).
    if (view === 'new-job-search-form') return getI18nLinkHref('/job/list', locale);
    if (typeof view === 'object' && 'keyword' in view) {
      return getI18nLinkHref(`/job/list/${slugify(view.keyword)}`, locale);
    }

    const isEdit = typeof view === 'object' && 'editSearch' in view;
    const filters = isEdit ? view.editSearch : (view).candidateSearch;
    const { keyword, locations } = filters;
    const keywordSlug = keyword ? `/${slugify(keyword)}` : '';

    const base = locations?.length
      ? getI18nLinkHref(`/job/list/location-${locations.map(slugify).join(',')}`, locale).replace(/%2C/g, ',')
      : getI18nLinkHref('/job/list', locale);

    return `${base}${keywordSlug}${isEdit ? '?edit=1' : ''}`;
  }, [locale]);

  // Navigate for real (soft, client-side). The view is derived from the resulting URL,
  // so the rendered content updates once the route changes — no manual state sync.
  // The `replace` option is used by post-fetch URL corrections (e.g. when /v2/jobs
  // `decomposedFilters` reveals the typed keyword was actually a location) so the
  // pre-correction URL doesn't bloat browser history.
  const setView = useCallback((view: CandidateView, options?: NavigateOptions) => {
    const url = buildViewUrl(view);
    if (options?.replace) {
      void router.replace(url, { withLocale: false });
    } else {
      void router.push(url, { withLocale: false });
    }
  }, [buildViewUrl, router]);

  // Convenience methods
  const navigateToNewSearch = useCallback(() => setView('new-search'), [setView]);
  const navigateToNewJobSearchForm = useCallback(() => setView('new-job-search-form'), [setView]);
  const navigateToBrowseAll = useCallback(() => setView('browse-all'), [setView]);
  const navigateToKeywordSearch = useCallback((keyword: string) => setView({ keyword }), [setView]);
  const navigateToCandidateSearch = useCallback(
    (filters: CandidateSearchFilters, options?: NavigateOptions) => setView({ candidateSearch: filters }, options),
    [setView],
  );
  const navigateToEditSearch = useCallback((filters: CandidateSearchFilters) => setView({ editSearch: filters }), [setView]);

  const value: CandidateNavigationContextValue = useMemo(() => ({
    currentView,
    setView,
    navigateToNewSearch,
    navigateToNewJobSearchForm,
    navigateToBrowseAll,
    navigateToKeywordSearch,
    navigateToCandidateSearch,
    navigateToEditSearch,
  }), [
    currentView, setView, navigateToNewSearch, navigateToNewJobSearchForm,
    navigateToBrowseAll,
    navigateToKeywordSearch, navigateToCandidateSearch, navigateToEditSearch,
  ]);

  return (
    <CandidateNavigationContext.Provider value={value}>
      {children}
    </CandidateNavigationContext.Provider>
  );
};

/**
 * @description Hook to access candidate navigation context.
 * Throws error if used outside of CandidateNavigationProvider.
 */
export const useCandidateNavigation = () => {
  const context = useContext(CandidateNavigationContext);

  if (!context) {
    throw new Error('useCandidateNavigation must be used within CandidateNavigationProvider');
  }

  return context;
};

/**
 * @description Optional variant used by components that can render both inside and
 * outside the CandidateNavigationProvider (e.g. standalone MC pages).
 */
export const useCandidateNavigationOptional = () => useContext(CandidateNavigationContext);
